import { useState, useEffect, useRef } from "react";
import { AppLayout } from "@/components/layout/AppLayout";
import { PageHeader } from "@/components/common/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AdminStatsCards } from "@/components/admin/AdminStatsCards";
import { VendorManagement } from "@/components/admin/VendorManagement";
import { BankerManagement } from "@/components/admin/BankerManagement";
import { ProductManagement } from "@/components/admin/ProductManagement";
import { DisbursementAnalytics } from "@/components/admin/DisbursementAnalytics";
import { AttendanceTracking } from "@/components/admin/AttendanceTracking";
import { OfficeManagement } from "@/components/admin/OfficeManagement";
import { UserManagement } from "@/components/admin/UserManagement";
import { QuestionnaireManagement } from "@/components/admin/QuestionnaireManagement";
import { ChecklistManagement } from "@/components/admin/ChecklistManagement";
import { IncomeManagement } from "@/components/admin/IncomeManagement";
import {
  ArrowLeft,
  CalendarIcon,
  Users,
  Filter,
  ChevronDown,
  ChevronUp,
  Building2,
  Briefcase,
  Package,
  TrendingUp,
  Calendar as CalendarDay,
  RefreshCw,
  UserCog,
  ClipboardList,
  FileCheck,
  DollarSign,
  Phone,
  MessageSquare,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  getAdminStats,
  DateRangeFilter,
  AdminStats,
  getCommitmentTargets,
} from "@/services/adminService";
import { getAllActiveUsers } from "@/services/userService";
import { getTodayAttendance } from "@/services/attendanceService";
import { ApprovedUser, Permission } from "@/types";
import { AttendanceRecord } from "@/types/attendance";
import { useAuth } from "@/contexts/AuthContext";
import { usePermissions } from "@/hooks/useRoles";
import {
  startOfDay,
  endOfDay,
  startOfMonth,
  endOfMonth,
  format,
} from "date-fns";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";

type PeriodType = "today" | "month" | "custom";

export default function AdminPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { hasPermission, loading: permissionsLoading } = usePermissions(
    user?.roles || [],
  );

  const [period, setPeriod] = useState<PeriodType>("today");
  const [customDateRange, setCustomDateRange] = useState<{
    from?: Date;
    to?: Date;
  }>({});
  const [teamMemberFilter, setTeamMemberFilter] = useState<string>("all");
  const [activeUsers, setActiveUsers] = useState<ApprovedUser[]>([]);
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState("attendance");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const touchStartY = useRef(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [stats, setStats] = useState<AdminStats>({
    totalLoginToday: 0,
    loginAmount: 0,
    totalDisbursementToday: 0,
    disbursedAmount: 0,
    totalVendorVisits: 0,
    totalLeadsStatusUpdated: 0,
    totalLeadsInPipeline: 0,
    totalNewLeads: 0,
    totalNewVendorsAdded: 0,
  });
  const [loading, setLoading] = useState(true);
  const [selectedUserAttendance, setSelectedUserAttendance] =
    useState<AttendanceRecord | null>(null);

  // Calculate date range based on period
  const getDateRange = (): DateRangeFilter => {
    const now = new Date();

    switch (period) {
      case "today":
        return {
          startDate: startOfDay(now),
          endDate: endOfDay(now),
        };
      case "month":
        return {
          startDate: startOfMonth(now),
          endDate: endOfMonth(now),
        };
      case "custom":
        // Include the whole "From" day; exclude the selected "To" day.
        // We set endDate to 1ms before startOfDay(To) because downstream
        // comparisons are inclusive (<= endDate).
        return {
          startDate: customDateRange.from
            ? startOfDay(customDateRange.from)
            : startOfDay(now),
          endDate: customDateRange.to
            ? new Date(startOfDay(customDateRange.to).getTime() - 1)
            : endOfDay(now),
        };
      default:
        return {
          startDate: startOfDay(now),
          endDate: endOfDay(now),
        };
    }
  };

  // Pull-to-refresh handlers
  const handleTouchStart = (e: React.TouchEvent) => {
    if (
      scrollContainerRef.current &&
      scrollContainerRef.current.scrollTop === 0
    ) {
      touchStartY.current = e.touches[0].clientY;
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (isRefreshing || !scrollContainerRef.current) return;

    const scrollTop = scrollContainerRef.current.scrollTop;
    if (scrollTop > 0) {
      setPullDistance(0);
      return;
    }

    const touchY = e.touches[0].clientY;
    const distance = touchY - touchStartY.current;

    if (distance > 0 && distance < 120) {
      setPullDistance(distance);
    }
  };

  const handleTouchEnd = async () => {
    if (pullDistance > 80) {
      // Haptic feedback on mobile devices
      if ("vibrate" in navigator) {
        navigator.vibrate(50);
      }

      setIsRefreshing(true);
      setPullDistance(0);

      try {
        await Promise.all([loadStats(), loadActiveUsers()]);
        toast.success("Data refreshed successfully");
      } catch (error) {
        toast.error("Failed to refresh data");
      } finally {
        setTimeout(() => setIsRefreshing(false), 500);
      }
    } else {
      setPullDistance(0);
    }
  };

  const handleManualRefresh = async () => {
    // Check if online
    if (!navigator.onLine) {
      toast.error("Cannot refresh while offline");
      return;
    }

    // Haptic feedback on mobile devices
    if ("vibrate" in navigator) {
      navigator.vibrate(50);
    }

    setIsRefreshing(true);
    try {
      await Promise.all([loadStats(), loadActiveUsers()]);
      toast.success("Data refreshed successfully");
    } catch (error) {
      toast.error("Failed to refresh data");
    } finally {
      setTimeout(() => setIsRefreshing(false), 500);
    }
  };

  // Load stats when period changes
  useEffect(() => {
    loadStats();
  }, [period, customDateRange, teamMemberFilter]);

  // Load active users on mount
  useEffect(() => {
    loadActiveUsers();
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      // Only if not typing in an input
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement
      ) {
        return;
      }

      // Cmd/Ctrl + R to refresh
      if ((e.metaKey || e.ctrlKey) && e.key === "r") {
        e.preventDefault();
        handleManualRefresh();
      }

      // Number keys 1-5 for tab navigation on desktop
      if (window.innerWidth >= 768) {
        const tabMap: { [key: string]: string } = {
          "1": "vendors",
          "2": "bankers",
          "3": "products",
          "4": "disbursement",
          "5": "attendance",
        };
        if (tabMap[e.key]) {
          setActiveTab(tabMap[e.key]);
        }
      }

      // F for filters toggle (mobile)
      if (e.key === "f" && window.innerWidth < 768) {
        setFiltersExpanded((prev) => !prev);
      }

      // T for today, M for month
      if (e.key === "t") {
        setPeriod("today");
      } else if (e.key === "m") {
        setPeriod("month");
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, []);

  // Online/offline detection
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      toast.success("Back online");
    };

    const handleOffline = () => {
      setIsOnline(false);
      toast.error("No internet connection", {
        duration: Infinity,
        id: "offline-toast",
      });
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  const loadActiveUsers = async () => {
    try {
      const usersData = await getAllActiveUsers();
      setActiveUsers(usersData);
    } catch (error) {
      console.error("Failed to load users:", error);
    }
  };

  const loadStats = async () => {
    setLoading(true);
    try {
      const dateRange = getDateRange();
      const adminStats = await getAdminStats(
        dateRange,
        teamMemberFilter === "all" ? undefined : teamMemberFilter,
        {
          userId: user?.id,
          userPhone: user?.phone,
          officeId: user?.officeId,
          role: user?.primaryRole,
          roles: user?.roles,
        },
      );

      // Fetch commitment targets based on period and filter
      const commitmentPeriod = period === "today" ? "today" : "month";
      const commitmentTargets = await getCommitmentTargets(
        commitmentPeriod,
        teamMemberFilter === "all" ? undefined : teamMemberFilter,
        {
          userId: user?.id,
          userPhone: user?.phone,
          officeId: user?.officeId,
          role: user?.primaryRole,
          roles: user?.roles,
        },
      );

      // Merge stats with targets
      setStats({
        ...adminStats,
        ...commitmentTargets,
      });
    } catch (error) {
      toast.error("Failed to load statistics");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Get selected user details
  const selectedUser =
    teamMemberFilter !== "all"
      ? activeUsers.find((u) => u.phone === teamMemberFilter)
      : null;

  // Contact handlers for selected user
  const handleCall = () => {
    if (selectedUser?.phone) {
      window.open(`tel:${selectedUser.phone}`);
    }
  };

  const handleWhatsApp = () => {
    if (selectedUser?.phone) {
      const formatCurrency = (amount: number) => {
        if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(1)}Cr`;
        if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
        return `₹${amount.toLocaleString("en-IN")}`;
      };

      const message = `Hi ${selectedUser.name},

Here's your performance summary:
• Login: ${stats.totalLoginToday} files${stats.loginTargetFiles ? ` (Target: ${stats.loginTargetFiles})` : ""}${stats.loginTargetAmount ? ` - Target: ${formatCurrency(stats.loginTargetAmount)}` : ""}
• Disbursement: ${stats.totalDisbursementToday} files${stats.disbursementTargetFiles ? ` (Target: ${stats.disbursementTargetFiles})` : ""}${stats.disbursementTargetAmount ? ` - Target: ${formatCurrency(stats.disbursementTargetAmount)}` : ""}
• New Leads: ${stats.totalNewLeads}
• Vendor Visits: ${stats.totalVendorVisits}

Keep up the great work! 🎯`;

      const waNum = selectedUser.phone.replace(/\D/g, "");
      const waFull =
        waNum.startsWith("91") && waNum.length > 10 ? waNum : "91" + waNum;
      window.open(
        `https://wa.me/${waFull}?text=${encodeURIComponent(message)}`,
      );
    }
  };

  // Helper to get attendance status display
  const getAttendanceStatusDisplay = () => {
    if (!selectedUserAttendance)
      return { label: "Pending", color: "text-gray-500" };

    // Handle explicit status values
    if (selectedUserAttendance.status === "holiday") {
      return { label: "Holiday", color: "text-blue-600" };
    }
    if (selectedUserAttendance.status === "absent") {
      return { label: "Absent", color: "text-red-600" };
    }
    if (selectedUserAttendance.status === "pending") {
      return { label: "Pending", color: "text-gray-500" };
    }

    // Handle present/late/away combinations
    const isLate = !!selectedUserAttendance.isLate;
    const isAway = selectedUserAttendance.checkInWithinRange === false;

    if (isLate && isAway) {
      return { label: "Late + Away", color: "text-amber-600" };
    }
    if (isLate) {
      return { label: "Late", color: "text-yellow-600" };
    }
    if (isAway) {
      return { label: "Away", color: "text-orange-600" };
    }
    return { label: "Present", color: "text-green-600" };
  };

  // Fetch attendance when team member changes
  useEffect(() => {
    const fetchAttendance = async () => {
      if (teamMemberFilter === "all") {
        setSelectedUserAttendance(null);
        return;
      }

      try {
        // Find the selected user's ID from their phone
        const selectedUser = activeUsers.find(
          (u) => u.phone === teamMemberFilter,
        );

        if (selectedUser?.userId) {
          const attendance = await getTodayAttendance(selectedUser.userId);
          setSelectedUserAttendance(attendance);
        }
      } catch (error) {
        console.error("Error fetching attendance:", error);
        setSelectedUserAttendance(null);
      }
    };

    fetchAttendance();
  }, [teamMemberFilter, activeUsers]);

  // Show loading while checking permissions to avoid flash
  if (
    permissionsLoading ||
    (user && !hasPermission(Permission.ACCESS_ADMIN_TOOLS))
  ) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-screen">
          {permissionsLoading ? (
            <RefreshCw className="h-8 w-8 animate-spin text-primary" />
          ) : (
            <p className="text-muted-foreground">
              You don't have permission to access this page.
            </p>
          )}
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div
        ref={scrollContainerRef}
        className="p-4 md:p-6 lg:p-8 pt-6 max-w-7xl mx-auto space-y-2 md:space-y-6 md:overflow-visible overflow-y-auto h-full"
      >
        {/* Header - Like Leads page */}
        <div className="flex items-center justify-between gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/tools")}
            className="shrink-0"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1 min-w-0">
            <PageHeader
              title="Admin Dashboard"
              subtitle="System administration and analytics"
            />
          </div>

          <div className="flex items-center gap-2">
            {/* Offline indicator */}
            {!isOnline && (
              <Badge variant="destructive" className="animate-pulse">
                Offline
              </Badge>
            )}
            {/* Refresh Button */}
            <Button
              variant="outline"
              size="icon"
              onClick={loadStats}
              disabled={loading}
              className="shrink-0"
            >
              <RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} />
            </Button>
          </div>
        </div>

        {/* Period Selection and Global Filter */}
        {/* Desktop View - Original Layout */}
        <Card className="hidden md:block">
          <CardContent className="p-6 space-y-6">
            {/* Period Selection */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <CalendarIcon className="h-4 w-4 text-muted-foreground" />
                <h3 className="font-medium text-sm">Date Range</h3>
              </div>
              <Tabs
                value={period}
                onValueChange={(v) => setPeriod(v as PeriodType)}
              >
                <TabsList className="grid w-full max-w-md grid-cols-3">
                  <TabsTrigger
                    value="today"
                    className="data-[state=active]:bg-green-500 data-[state=active]:text-white"
                  >
                    Today
                  </TabsTrigger>
                  <TabsTrigger
                    value="month"
                    className="data-[state=active]:bg-green-500 data-[state=active]:text-white"
                  >
                    This Month
                  </TabsTrigger>
                  <TabsTrigger
                    value="custom"
                    className="data-[state=active]:bg-green-500 data-[state=active]:text-white"
                  >
                    Custom Range
                  </TabsTrigger>
                </TabsList>

                {/* Custom Date Range Picker */}
                {period === "custom" && (
                  <div className="mt-4 flex gap-2 items-center flex-wrap">
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            "justify-start text-left font-normal",
                            !customDateRange.from && "text-muted-foreground",
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {customDateRange.from ? (
                            format(customDateRange.from, "PPP")
                          ) : (
                            <span>From date</span>
                          )}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={customDateRange.from}
                          onSelect={(date) =>
                            setCustomDateRange((prev) => ({
                              ...prev,
                              from: date,
                            }))
                          }
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>

                    <span className="text-muted-foreground">to</span>

                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            "justify-start text-left font-normal",
                            !customDateRange.to && "text-muted-foreground",
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {customDateRange.to ? (
                            format(customDateRange.to, "PPP")
                          ) : (
                            <span>To date</span>
                          )}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={customDateRange.to}
                          onSelect={(date) =>
                            setCustomDateRange((prev) => ({
                              ...prev,
                              to: date,
                            }))
                          }
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                )}
              </Tabs>
            </div>

            {/* Divider */}
            <div className="border-t" />

            {/* Global Team Member Filter */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-muted-foreground" />
                <h3 className="font-medium text-sm">Team Member Filter</h3>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
                <Select
                  value={teamMemberFilter}
                  onValueChange={setTeamMemberFilter}
                >
                  <SelectTrigger className="w-full sm:w-[280px]">
                    <SelectValue placeholder="Filter by team member" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Team Members</SelectItem>
                    {activeUsers.map((user) => (
                      <SelectItem key={user.phone} value={user.phone}>
                        {user.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted/50 px-3 py-2 rounded-md">
                  <div className="h-2 w-2 rounded-full bg-green-500" />
                  <span>Applies to: Stats, Vendors & Disbursements</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Mobile View - Collapsible Filters */}
        <div className="block md:hidden">
          {/* Floating Filter Button */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setFiltersExpanded(!filtersExpanded)}
            className="w-full mb-3 bg-background border-2 justify-between"
          >
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4" />
              <span>Filters</span>
              {teamMemberFilter !== "all" && (
                <Badge
                  variant="secondary"
                  className="ml-1 bg-green-500 text-white"
                >
                  Active
                </Badge>
              )}
            </div>
            {filtersExpanded ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </Button>

          {/* Collapsible Filter Content */}
          <AnimatePresence>
            {filtersExpanded && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
              >
                <Card className="mb-3">
                  <CardContent className="p-3 space-y-3">
                    {/* Compact date range */}
                    <div>
                      <p className="text-xs font-medium mb-2 flex items-center gap-1">
                        <CalendarIcon className="h-3 w-3" />
                        Period
                      </p>
                      <div className="grid grid-cols-3 gap-2">
                        <Button
                          size="sm"
                          variant={period === "today" ? "default" : "outline"}
                          onClick={() => setPeriod("today")}
                          className={cn(
                            "text-xs h-8",
                            period === "today" &&
                              "bg-green-500 hover:bg-green-600",
                          )}
                        >
                          Today
                        </Button>
                        <Button
                          size="sm"
                          variant={period === "month" ? "default" : "outline"}
                          onClick={() => setPeriod("month")}
                          className={cn(
                            "text-xs h-8",
                            period === "month" &&
                              "bg-green-500 hover:bg-green-600",
                          )}
                        >
                          Month
                        </Button>
                        <Button
                          size="sm"
                          variant={period === "custom" ? "default" : "outline"}
                          onClick={() => setPeriod("custom")}
                          className={cn(
                            "text-xs h-8",
                            period === "custom" &&
                              "bg-green-500 hover:bg-green-600",
                          )}
                        >
                          Custom
                        </Button>
                      </div>

                      {/* Custom date pickers for mobile */}
                      {period === "custom" && (
                        <div className="mt-3 space-y-2">
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                className="w-full justify-start text-xs h-8"
                              >
                                <CalendarIcon className="mr-2 h-3 w-3" />
                                {customDateRange.from
                                  ? format(customDateRange.from, "PP")
                                  : "From date"}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent
                              className="w-auto p-0"
                              align="start"
                            >
                              <Calendar
                                mode="single"
                                selected={customDateRange.from}
                                onSelect={(date) =>
                                  setCustomDateRange((prev) => ({
                                    ...prev,
                                    from: date,
                                  }))
                                }
                                initialFocus
                              />
                            </PopoverContent>
                          </Popover>

                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                className="w-full justify-start text-xs h-8"
                              >
                                <CalendarIcon className="mr-2 h-3 w-3" />
                                {customDateRange.to
                                  ? format(customDateRange.to, "PP")
                                  : "To date"}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent
                              className="w-auto p-0"
                              align="start"
                            >
                              <Calendar
                                mode="single"
                                selected={customDateRange.to}
                                onSelect={(date) =>
                                  setCustomDateRange((prev) => ({
                                    ...prev,
                                    to: date,
                                  }))
                                }
                                initialFocus
                              />
                            </PopoverContent>
                          </Popover>
                        </div>
                      )}
                    </div>

                    {/* Team member filter */}
                    <div>
                      <p className="text-xs font-medium mb-2 flex items-center gap-1">
                        <Users className="h-3 w-3" />
                        Team Filter
                      </p>
                      <Select
                        value={teamMemberFilter}
                        onValueChange={setTeamMemberFilter}
                      >
                        <SelectTrigger className="h-8 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Team Members</SelectItem>
                          {activeUsers.map((user) => (
                            <SelectItem key={user.phone} value={user.phone}>
                              {user.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Filter indicator */}
                    {teamMemberFilter !== "all" && (
                      <div className="flex items-center gap-2 text-xs bg-green-50 dark:bg-green-950 p-2 rounded-md">
                        <div className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
                        <span className="text-green-700 dark:text-green-300">
                          Filtering by:{" "}
                          {
                            activeUsers.find(
                              (u) => u.phone === teamMemberFilter,
                            )?.name
                          }
                        </span>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Status and Stats Section */}
        <div className="space-y-1">
          <h3 className="font-semibold text-green-800 text-base">
            {selectedUser ? (
              <>
                {selectedUser.name} Status{" "}
                <span
                  className={`font-semibold ${getAttendanceStatusDisplay().color}`}
                >
                  ({getAttendanceStatusDisplay().label})
                </span>
              </>
            ) : (
              "All User Status"
            )}
          </h3>

          <AdminStatsCards stats={stats} loading={loading} />
        </div>

        {/* Contact Buttons for Selected User */}
        {selectedUser && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="flex gap-3"
          >
            <Button
              onClick={handleCall}
              className="flex-1 gap-2 h-10 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-900 font-medium"
            >
              <Phone className="h-4 w-4" />
              Call for Status
            </Button>
            <Button
              onClick={handleWhatsApp}
              className="flex-1 gap-2 h-10 rounded-xl bg-green-600 hover:bg-green-700 text-white font-medium"
            >
              <MessageSquare className="h-4 w-4" />
              WhatsApp Status
            </Button>
          </motion.div>
        )}

        {/* Management Sections */}
        {/* Mobile View - Tools Grid Card */}
        <div className="block md:hidden">
          <Card className="border shadow-sm">
            <CardContent className="p-4">
              <p className="text-xs font-semibold text-muted-foreground mb-3 uppercase tracking-wide">
                Admin Tools
              </p>
              <div className="grid grid-cols-5 gap-3">
                {[
                  {
                    value: "attendance",
                    label: "Attend",
                    icon: CalendarDay,
                    color: "bg-teal-100 text-teal-600",
                  },
                  {
                    value: "offices",
                    label: "Offices",
                    icon: Building2,
                    color: "bg-orange-100 text-orange-600",
                  },
                  {
                    value: "users",
                    label: "Users",
                    icon: UserCog,
                    color: "bg-rose-100 text-rose-600",
                  },
                  {
                    value: "vendors",
                    label: "Vendors",
                    icon: Building2,
                    color: "bg-blue-100 text-blue-600",
                  },
                  {
                    value: "bankers",
                    label: "Bankers",
                    icon: Briefcase,
                    color: "bg-indigo-100 text-indigo-600",
                  },
                  {
                    value: "products",
                    label: "Products",
                    icon: Package,
                    color: "bg-purple-100 text-purple-600",
                  },
                  {
                    value: "disbursement",
                    label: "Disbursed",
                    icon: TrendingUp,
                    color: "bg-green-100 text-green-600",
                  },
                  {
                    value: "questionnaires",
                    label: "Quest",
                    icon: ClipboardList,
                    color: "bg-cyan-100 text-cyan-600",
                  },
                  {
                    value: "checklists",
                    label: "Checks",
                    icon: FileCheck,
                    color: "bg-amber-100 text-amber-600",
                  },
                  {
                    value: "income",
                    label: "Income",
                    icon: DollarSign,
                    color: "bg-emerald-100 text-emerald-600",
                  },
                ].map((tool, index) => (
                  <motion.button
                    key={tool.value}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.03 }}
                    onClick={() => {
                      if ("vibrate" in navigator) navigator.vibrate(30);
                      setActiveTab(tool.value);
                    }}
                    className={cn(
                      "flex flex-col items-center justify-center p-2 rounded-xl transition-all tap-highlight-green",
                      activeTab === tool.value
                        ? "bg-green-500 text-white shadow-md ring-2 ring-green-300"
                        : "bg-muted/40 text-muted-foreground active:bg-muted",
                    )}
                  >
                    <div
                      className={cn(
                        "p-1.5 rounded-lg mb-1",
                        activeTab === tool.value ? "bg-white/20" : tool.color,
                      )}
                    >
                      <tool.icon className="h-4 w-4" />
                    </div>
                    <span className="text-[9px] font-medium whitespace-nowrap leading-tight">
                      {tool.label}
                    </span>
                  </motion.button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Full-screen tool content */}
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.2 }}
              className="mt-4 min-h-[400px]"
            >
              {activeTab === "attendance" && <AttendanceTracking />}
              {activeTab === "offices" && <OfficeManagement />}
              {activeTab === "users" && <UserManagement />}
              {activeTab === "vendors" && (
                <VendorManagement teamMemberFilter={teamMemberFilter} />
              )}
              {activeTab === "bankers" && <BankerManagement />}
              {activeTab === "products" && <ProductManagement />}
              {activeTab === "disbursement" && (
                <DisbursementAnalytics teamMemberFilter={teamMemberFilter} />
              )}
              {activeTab === "questionnaires" && <QuestionnaireManagement />}
              {activeTab === "checklists" && <ChecklistManagement />}
              {activeTab === "income" && <IncomeManagement />}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Desktop View - Original Tabs Layout */}
        <Tabs defaultValue="attendance" className="space-y-4 hidden md:block">
          <TabsList className="grid w-full grid-cols-10 h-auto p-1">
            <TabsTrigger
              value="attendance"
              className="data-[state=active]:bg-green-500 data-[state=active]:text-white data-[state=active]:shadow-md"
            >
              Attendance
            </TabsTrigger>
            <TabsTrigger
              value="offices"
              className="data-[state=active]:bg-green-500 data-[state=active]:text-white data-[state=active]:shadow-md"
            >
              Offices
            </TabsTrigger>
            <TabsTrigger
              value="users"
              className="data-[state=active]:bg-green-500 data-[state=active]:text-white data-[state=active]:shadow-md"
            >
              Users
            </TabsTrigger>
            <TabsTrigger
              value="vendors"
              className="data-[state=active]:bg-green-500 data-[state=active]:text-white data-[state=active]:shadow-md"
            >
              Vendors
            </TabsTrigger>
            <TabsTrigger
              value="bankers"
              className="data-[state=active]:bg-green-500 data-[state=active]:text-white data-[state=active]:shadow-md"
            >
              Bankers
            </TabsTrigger>
            <TabsTrigger
              value="products"
              className="data-[state=active]:bg-green-500 data-[state=active]:text-white data-[state=active]:shadow-md"
            >
              Products
            </TabsTrigger>
            <TabsTrigger
              value="disbursement"
              className="data-[state=active]:bg-green-500 data-[state=active]:text-white data-[state=active]:shadow-md"
            >
              Disbursement
            </TabsTrigger>
            <TabsTrigger
              value="questionnaires"
              className="data-[state=active]:bg-green-500 data-[state=active]:text-white data-[state=active]:shadow-md"
            >
              Questionnaires
            </TabsTrigger>
            <TabsTrigger
              value="checklists"
              className="data-[state=active]:bg-green-500 data-[state=active]:text-white data-[state=active]:shadow-md"
            >
              Checklists
            </TabsTrigger>
            <TabsTrigger
              value="income"
              className="data-[state=active]:bg-green-500 data-[state=active]:text-white data-[state=active]:shadow-md"
            >
              Income
            </TabsTrigger>
          </TabsList>

          <TabsContent value="attendance">
            <AttendanceTracking />
          </TabsContent>

          <TabsContent value="offices">
            <OfficeManagement />
          </TabsContent>

          <TabsContent value="users">
            <UserManagement />
          </TabsContent>

          <TabsContent value="vendors">
            <VendorManagement teamMemberFilter={teamMemberFilter} />
          </TabsContent>

          <TabsContent value="bankers">
            <BankerManagement />
          </TabsContent>

          <TabsContent value="products">
            <ProductManagement />
          </TabsContent>

          <TabsContent value="disbursement">
            <DisbursementAnalytics teamMemberFilter={teamMemberFilter} />
          </TabsContent>

          <TabsContent value="questionnaires">
            <QuestionnaireManagement />
          </TabsContent>

          <TabsContent value="checklists">
            <ChecklistManagement />
          </TabsContent>

          <TabsContent value="income">
            <IncomeManagement />
          </TabsContent>
        </Tabs>
      </div>
    </AppLayout>
  );
}
